import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES, VALUES } from "../../lib/data";
import { Reveal, Overline, Icon } from "../ui-kit";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="about" ref={ref} className="relative py-24 md:py-32 bg-[#050505]" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Image with parallax + spotlight framing */}
          <Reveal className="order-2 lg:order-1">
            <div className="relative aspect-[4/5] max-w-md overflow-hidden clip-notch border border-zinc-800">
              <motion.img style={{ y: imgY }} src={IMAGES.worker} alt="Sobtrex professional"
                className="w-full h-[125%] object-cover grayscale contrast-125" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-display text-2xl font-black uppercase text-white">Built for Professionals.</p>
                <div className="mt-2 h-0.5 w-16 bg-orange-500" />
              </div>
            </div>
          </Reveal>

          <div className="order-1 lg:order-2">
            <Overline index="05">About Sobtrex</Overline>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
              Built for <span className="text-orange-500">Professionals.</span>
            </h2>
            <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
              Sobtrex is an emerging industrial tools brand focused on delivering dependable,
              high-performance power tools for professionals and businesses. Our mission is to combine
              quality, durability, innovation, and value while building long-term partnerships with
              dealers, distributors, and industrial customers.
            </p>
            <p className="mt-4 text-base text-zinc-500 leading-relaxed">
              Beginning with a carefully selected portfolio of professional corded and cordless power tools,
              Sobtrex aims to expand into a complete industrial ecosystem including woodworking equipment,
              garden tools, accessories, storage solutions, and professional safety products.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              {VALUES.map((v) => (
                <span key={v} className="text-xs font-bold uppercase tracking-wider text-zinc-400 border border-zinc-800 px-4 py-2 hover:border-orange-500/50 hover:text-orange-500 transition-colors">
                  {v}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Mission / Vision cards */}
        <div className="mt-20 grid md:grid-cols-2 gap-6">
          {[
            { t: "Our Mission", icon: "Target", d: "Deliver reliable, innovative, and affordable professional tools for every workshop and job site." },
            { t: "Our Vision", icon: "Eye", d: "Become one of India's most trusted global-quality industrial tools brands." },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="group h-full bg-[#101012] border border-zinc-800 hover:border-orange-500/50 p-10 transition-colors">
                <div className="h-14 w-14 flex items-center justify-center bg-black border border-zinc-800 text-orange-500 group-hover:border-orange-500/50 transition-colors">
                  <Icon name={c.icon} size={28} />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">{c.t}</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
